import React, {
  forwardRef,
  memo,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SearchFilterToggleProps } from '@msc-b2c/interfaces/types';
import {
  AppDispatch,
  AppliedStylesContext,
  breakpoints,
  checkDealsFilterType,
  getDealsFilters,
  handleKeyDown,
  IconsContext,
  isBehindScenes,
  isTherePortChips,
  RootState,
  setSearchButtonClicked,
  useWindowSize,
} from '@msc-b2c/shared-utils';

import { Icon } from '../icons/Icon';
import { Text } from '../text/Text';
import { SearchInput } from './components/SearchInput';

import styles from './searchfiltertoggle.module.scss';
import { Button } from '../button/Button';
import { FilterTypes } from '@msc-b2c/interfaces/enums';

export const SearchFilterToggle = memo(
  forwardRef(function SearchFilterToggle(
    {
      id,
      filterType,
      accessibilityLabel,
      filterUpperLabel,
      filterPlaceholder,
      filterIcon = '',
      filterIconAriaLabel = '',
      searchInputPlaceholder = '',
      searchInputIcon = '',
      searchInputIconAltText = '',
      searchTerm = '',
      handleSearchInput,
      enableliveSearch = false,
      breakPointToShowSearchInput = breakpoints.lg,
      styleOption = '',
      handleToggle,
      isOpen,
      role = 'button',
      automationId,
      shouldRenderChildren = false,
      children,
      IconCloseTitle,
      IconOpenTitle,
    }: Readonly<SearchFilterToggleProps>,
    ref: React.Ref<HTMLDivElement>
  ) {
    const windowSize = useWindowSize();
    const isOverBreakpoint = windowSize.width >= breakPointToShowSearchInput;
    const { isAllFiltersActive } = useSelector((state: RootState) => state.searchBarState);
    const dispatch = useDispatch<AppDispatch>();
    const isDealsPage = getDealsFilters().length > 0;
    const classWrapper = 'cmp-search-filter__wrapper';

    const wrapperRef = useRef<HTMLDivElement>(null);
    const [indexType, setIndexType] = useState('itinerary');
    const {
      arrowUpIconSelect = '',
      arrowUpAria = '',
      arrowDownSelect = '',
      arrowDownAria = '',
      arrowRightSelect = '',
      arrowRightAria = '',
    } = useContext(IconsContext);
    const { searchbarStyles } = useContext(AppliedStylesContext);

    const memoizedHandleToggle = useCallback(() => {
      if (!handleToggle) return;

      handleToggle();

      dispatch(setSearchButtonClicked(false));
    }, [dispatch, handleToggle]);

    const memoizedHandleSearchInput = useCallback(
      (value: string) => handleSearchInput && handleSearchInput(value),
      [handleSearchInput]
    );

    const isDisabled = () => {
      let filterTypeSelected: boolean;

      if (isBehindScenes() && indexType === 'excursions') {
        filterTypeSelected = true;
      } else if (
        isTherePortChips() &&
        indexType === 'excursions' &&
        filterType !== FilterTypes.CATEGORY
      ) {
        filterTypeSelected = true;
      } else if (filterType === FilterTypes.DATE) {
        return false;
      } else {
        filterTypeSelected = checkDealsFilterType(filterType);
      }

      return (isTherePortChips() || isDealsPage) && filterTypeSelected;
    };

    useEffect(() => {
      if (wrapperRef.current) {
        const container = wrapperRef.current.closest('.cmp-searchbarcontainer');
        if (container) {
          const relatedContentWrapper = container.querySelector('.cmp-search-content__wrapper');
          if (relatedContentWrapper) {
            const attr = relatedContentWrapper.getAttribute('data-index-type');
            if (attr) {
              setIndexType(attr);
            }
          }
        }
      }
    }, []);
    useImperativeHandle(ref, () => wrapperRef.current as HTMLDivElement);

    return (
      <div className={classWrapper}>
        <div
          id={id}
          className={`${styles['cmp-search-filter']} ${styleOption} ${searchbarStyles
            .split(' ')
            .map((cssClass: string) => styles[cssClass])
            .join(' ')} ${isDisabled() ? 'disabled-filter' : ''}`}
          aria-label={isOpen ? IconCloseTitle : IconOpenTitle}
          onClick={isDisabled() ? undefined : memoizedHandleToggle}
          onKeyDown={(event) => handleKeyDown(event, memoizedHandleToggle)}
          tabIndex={0}
          role={role}
          data-filter-type={filterType}
          data-is-filter-active={isOpen}
          data-automation-id={automationId}
          ref={wrapperRef}
        >
          <div className={styles['cmp-search-filter__toggle']}>
            {filterUpperLabel && (
              <Text
                text={filterUpperLabel}
                styleOption={`cmp-text__xsmall ${styles['cmp-search-filter__toggle-upperlabel']}`}
                data-testid="upperfilterlabel"
                tagHtml="p"
              />
            )}
            {isOpen && isOverBreakpoint && enableliveSearch && handleSearchInput && (
              <SearchInput
                isOpen={isOpen}
                filterIcon={filterIcon}
                filterIconAriaLabel={filterIconAriaLabel}
                searchInputIcon={searchInputIcon}
                searchInputIconAltText={searchInputIconAltText}
                searchInputPlaceholder={searchInputPlaceholder}
                arrowUpIconSelect={arrowUpIconSelect}
                arrowUpAria={arrowUpAria}
                arrowDownSelect={arrowDownSelect}
                arrowDownAria={arrowDownAria}
                searchTerm={searchTerm}
                memoizedHandleSearchInput={memoizedHandleSearchInput}
                IconCloseTooltip={IconCloseTitle ?? ''}
              />
            )}
            {(!enableliveSearch || !isOverBreakpoint || !isOpen) && (
              <div className={styles['cmp-search-filter__toggle-placeholder']}>
                <Text
                  text={filterPlaceholder}
                  styleOption={`cmp-text__normal ${styles['cmp-search-filter__toggle-placeholder-label']}`}
                  tagHtml="p"
                />

                {!isAllFiltersActive ? (
                  <Button
                    icon={(isOpen && arrowUpIconSelect) || filterIcon || arrowDownSelect || ''}
                    iconAriaLabel={
                      (isOpen && arrowUpIconSelect) || filterIconAriaLabel || arrowDownAria
                    }
                    styleOption={
                      'cmp-button__secondary -icon-only -no-outline -negative cmp-search-filter__toggle-icon'
                    }
                    tooltipText={isOpen ? IconCloseTitle : IconOpenTitle}
                    ariaHidden={true}
                  />
                ) : (
                  arrowRightSelect && (
                    <Icon
                      icon={arrowRightSelect}
                      ariaLabel={arrowRightAria}
                    />
                  )
                )}
              </div>
            )}
          </div>
        </div>
        {shouldRenderChildren && children}
      </div>
    );
  })
);
